import { BankReconciliationRow } from "./bankReconciliationViewConfig";

export enum BankReconciliationSort {
    ENTITY_ASC = "entityAsc",
    ENTITY_DESC = "entityDesc",
    ACCOUNT_ASC = "accountAsc",
    ACCOUNT_DESC = "accountDesc",
    START_DATE_ASC = "startDateAsc",
    START_DATE_DESC = "startDateDesc",
    BEGINNING_BALANCE_ASC = "beginningBalanceAsc",
    BEGINNING_BALANCE_DESC = "beginningBalanceDesc",
    END_DATE_ASC = "endDateAsc",
    END_DATE_DESC = "endDateDesc",
    ENDING_BALANCE_ASC = "endingBalanceAsc",
    ENDING_BALANCE_DESC = "endingBalanceDesc",
}

export const DEFAULT_BANK_RECONCILIATION_SORT =
    BankReconciliationSort.ENTITY_ASC;

const ASCENDING_SORTS = new Set<BankReconciliationSort>([
    BankReconciliationSort.ENTITY_ASC,
    BankReconciliationSort.ACCOUNT_ASC,
    BankReconciliationSort.START_DATE_ASC,
    BankReconciliationSort.BEGINNING_BALANCE_ASC,
    BankReconciliationSort.END_DATE_ASC,
    BankReconciliationSort.ENDING_BALANCE_ASC,
]);

const isAscending = (sort: BankReconciliationSort): boolean =>
    ASCENDING_SORTS.has(sort);

/**
 * Compares two nullable values so that missing entries always compare as the
 * lowest value. Returned as the ascending-direction ordering; callers invert it
 * for descending sorts. This makes missing dates/balances land at the beginning
 * when ascending and at the end when descending.
 */
function compareNullableAscending<T>(
    a: T | null | undefined,
    b: T | null | undefined,
    compare: (first: T, second: T) => number,
): number {
    const isAMissing = a === null || a === undefined;
    const isBMissing = b === null || b === undefined;
    if (isAMissing && isBMissing) {
        return 0;
    }
    if (isAMissing) {
        return -1;
    }
    if (isBMissing) {
        return 1;
    }
    return compare(a, b);
}

interface CompareByColumnParams {
    a: BankReconciliationRow;
    b: BankReconciliationRow;
    sort: BankReconciliationSort;
    entityOrderByRowId: Map<BankReconciliationRow["id"], number>;
}

function compareByColumn({
    a,
    b,
    sort,
    entityOrderByRowId,
}: CompareByColumnParams): number {
    switch (sort) {
        case BankReconciliationSort.ENTITY_ASC:
        case BankReconciliationSort.ENTITY_DESC:
            return (
                (entityOrderByRowId.get(a.id) ?? 0) -
                (entityOrderByRowId.get(b.id) ?? 0)
            );
        case BankReconciliationSort.ACCOUNT_ASC:
        case BankReconciliationSort.ACCOUNT_DESC: {
            const nameComparison = (
                a.financialAccount.name ?? ""
            ).localeCompare(b.financialAccount.name ?? "");
            if (nameComparison !== 0) {
                return nameComparison;
            }
            return (a.financialAccount.accountNumberMask ?? "").localeCompare(
                b.financialAccount.accountNumberMask ?? "",
                undefined,
                { numeric: true },
            );
        }
        case BankReconciliationSort.START_DATE_ASC:
        case BankReconciliationSort.START_DATE_DESC:
            return compareNullableAscending(
                a.openingDate || undefined,
                b.openingDate || undefined,
                (first, second) => first.localeCompare(second),
            );
        case BankReconciliationSort.BEGINNING_BALANCE_ASC:
        case BankReconciliationSort.BEGINNING_BALANCE_DESC:
            return compareNullableAscending(
                a.openingBalance,
                b.openingBalance,
                (first, second) => first - second,
            );
        case BankReconciliationSort.END_DATE_ASC:
        case BankReconciliationSort.END_DATE_DESC:
            return compareNullableAscending(
                a.closingDate,
                b.closingDate,
                (first, second) => first.localeCompare(second),
            );
        case BankReconciliationSort.ENDING_BALANCE_ASC:
        case BankReconciliationSort.ENDING_BALANCE_DESC:
            return compareNullableAscending(
                a.closingBalance,
                b.closingBalance,
                (first, second) => first - second,
            );
        default:
            return 0;
    }
}

/**
 * Sorts only the top-level account rows. Each row's nested `reconciliations`
 * array is left untouched so sorting never applies inside an account.
 */
export function sortBankReconciliationRows(
    rows: BankReconciliationRow[],
    sort: BankReconciliationSort,
): BankReconciliationRow[] {
    const originalIndexByRowId = new Map(
        rows.map((row, index) => [row.id, index]),
    );

    const entityOrderByRowId = new Map<BankReconciliationRow["id"], number>();
    const entityOrderByEntityId = new Map<number, number>();
    for (const row of rows) {
        const entityKey = row.entity?.id ?? -1;
        if (!entityOrderByEntityId.has(entityKey)) {
            entityOrderByEntityId.set(entityKey, entityOrderByEntityId.size);
        }
        entityOrderByRowId.set(
            row.id,
            entityOrderByEntityId.get(entityKey) ?? 0,
        );
    }

    const directionMultiplier = isAscending(sort) ? 1 : -1;

    return [...rows].sort((a, b) => {
        const comparison = compareByColumn({ a, b, sort, entityOrderByRowId });
        if (comparison !== 0) {
            return comparison * directionMultiplier;
        }
        const originalOrderComparison =
            (originalIndexByRowId.get(a.id) ?? 0) -
            (originalIndexByRowId.get(b.id) ?? 0);
        return originalOrderComparison;
    });
}
